import { SignalFunc, signal } from "../signal.js";
import { mixin } from "../mixin.js";
import {bufferToHex} from "../../util/CoreUtils.js";

export const RPC_OP_HELLO = 14;

export const RPC_OP_CALL = 1;
export const RPC_OP_ANSWER = 2;
export const RPC_OP_ANSWERERROR = 3;
export const RPC_OP_ANSWERITER = 12;
export const RPC_OP_EVENT = 13;

export const RPC_OP_FAIL = 4;
export enum RpcOp {
    HELLO = 14,
    METHODCALL = 1,
    ANSWER = 2,
    ANSWERERROR = 3,
    ANSWERITER = 12,
    EVENT = 13,
    FAIL = 4,
    PING = 5,
    PONG = 6,
    GETPROP = 7,
    SETPROP = 8,
    SUBSCRIBE = 9,
    UNSUBSCRIBE = 10,
    INTROSPECT = 11,
    SEND = 15,
    KEX = 16,
    SIGNED = 17,
}
export const RpcOpNames = [undefined, "METHODCALL", "ANSWER", "ANSWERERROR", "FAIL", "PING", "PONG", "GETPROP", "SETPROP", "SUBSCRIBE", "UNSUBSCRIBE", "INTROSPECT", "ANSWERITER", "EVENT", "HELLO", "SEND", "KEX", "SIGNED"];
export enum RpcField {
    OPCODE = 1,
    TARGET_NODE_ID = 2,
    OBJECT_ID = 3,
    INTERFACE_ID = 4,
    METHOD = 5,
    ARGS = 6,
    CONTEXT = 7,

}
export type IntrospectionInterfaceInfo = {
    interfaceId: string,
    methods: {
        methodName: string
    }[],
    properties: {
        name: string,
        get: boolean,
        set: boolean,
    }[],
}
/*
const OP_PING = 5;
const OP_PONG = 6;

const OP_METHODCALL = 1;
const OP_GETPROP = 7;
const OP_SETPROP = 8;
const OP_SUBSCRIBE = 9;
const OP_UNSUBSCRIBE = 10;
const OP_INTROSPECT = 11;

const OP_RESOLVE = 2;
const OP_REJECT = 3;
const OP_PROGRESS = 12;
const OP_EVENT = 13;

const OP_FAIL = 4;
*/

export type AddContext<Interface, Ctx> = {
    [Key in keyof Interface]: Interface[Key] extends (...args: any) => any ?
         (ctx: Ctx, ...args: Parameters<Interface[Key]>) => ReturnType<Interface[Key]> :
         Interface[Key]
}

export class RpcError extends Error {
    constructor(public readonly errorCode: number, public readonly errorMessage: string, public readonly calleeName?: string) {
        super(`[${errorCode}] **${errorMessage}** in ${calleeName}`);
    }
}
export class RpcConnFailed extends RpcError {
}
export class RpcTimeout extends RpcError {
}
export class RpcRemoteError extends RpcError {
}
function translateError(requestId: number, error: any) {
    if (error instanceof RpcError) {
        return [RpcOp.ANSWERERROR, requestId, error.errorCode, error.errorMessage];
    } else {
        console.error("Unhandled Exception:",error);
        return [RpcOp.ANSWERERROR, requestId, 0, ""+error];
    }
}

export interface RpcCallableNode<CallContext> {
    executeRpcCall(opCode: RpcOp, objectId: string, interfaceId: string, method: string, args: any[], context: CallContext, timeout?: number): Promise<any> | AsyncIterator<any>;
    executeRpcCallNoResponse(opCode: RpcOp, objectId: string, interfaceId: string, method: string, args: any[], context: CallContext);
}

@mixin
export abstract class AbstractRpcTransmitter<CallContext> {
    static Mixin : Function
    abstract sendRpcCall(opCode: RpcOp, nodeId: string, objectId: string, interfaceId: string, method: string, args: any[], context: CallContext, timeout?: number): Promise<any> | AsyncIterator<any>;
    abstract sendRpcCallNoResponse(opCode: RpcOp, nodeId: string, objectId: string, interfaceId: string, method: string, args: any[], context: CallContext);

    rpcEvent(nodeId: string, objectId: string, interfaceId: string, method: string, args: any[], context: CallContext) {
        this.sendRpcCallNoResponse(RpcOp.EVENT, nodeId, objectId, interfaceId, method, args, context);
    }
    rpcMethodCall(nodeId: string, objectId: string, interfaceId: string, method: string, args: any[], context: CallContext, timeout?: number): Promise<any> {
        return this.sendRpcCall(RpcOp.METHODCALL, nodeId, objectId, interfaceId, method, args, context, timeout) as Promise<any>;
    }
    rpcSubscribe(nodeId: string, objectId: string, interfaceId: string, method: string, args: any[], context: CallContext, timeout?: number): AsyncIterator<any> {
        return this.sendRpcCall(RpcOp.SUBSCRIBE, nodeId, objectId, interfaceId, method, args, context, timeout) as AsyncIterator<any>;
    }
    rpcIntrospect(nodeId: string, objectId: string, interfaceId: string, context: CallContext, timeout?: number) {
        return this.sendRpcCall(RpcOp.INTROSPECT, nodeId, objectId, interfaceId, '', [], context, timeout);
    }
    getProxy<Interface>(nodeId: string, objectId: string, interfaceId: string, context: CallContext, timeout?: number): Interface {
        const self = this;
        return new Proxy({}, {
            get(target, propName: string | symbol, receiver) {
                if (typeof propName !== 'string') throw "symbols not supported";
                if (propName === 'META') return {nodeId, objectId, interfaceId, context, timeout};
                return function(...args) {
                    return self.rpcMethodCall(nodeId, objectId, interfaceId, propName, args, context, timeout);
                };
            }
        }) as Interface;
    }
}

export type Signature = [ Uint8Array, Uint8Array, Uint8Array ];
export interface ICrypto {
    signWithClientKey(dataType: string, data: any): Promise<Signature>;
    verifyUnwrap(expectedDataType: string, signature: Signature): Promise<{ key: Uint8Array; data: any; }>
    keyExchangeRequest(data: any): Promise<[Uint8Array, any]>;
    keyExchange(peerKey: Uint8Array, dhKey: any): Promise<any>;
    aencryptSession(sessionKey: any, data: Uint8Array): Promise<Uint8Array>;
    adecryptSession(sessionKey: any, data: Uint8Array): Promise<Uint8Array>;
}

class AsyncIteratorProxy<T> implements AsyncIterator<T> {
    private queue : IteratorResult<T, T>[] = [];
    private resolve_ : Function | null = null;
    private reject_ : Function | null = null;
    private shouldReject : any = null;
    constructor(private unsubscribeCallback: Function) {}
    push(value: T, done = false) {
        if (this.resolve_) {
            this.resolve_({value, done});
            this.resolve_ = null; this.reject_ = null;
        } else {
            this.queue.push({value, done});
        }
    }
    reject(value: any) {
        if (this.reject_) {
            this.reject_(value);
        } else {
            this.shouldReject = value;
        }
    }
    next(...args: [] | [undefined]): Promise<IteratorResult<T, T>> {
        if (this.shouldReject !== null) {
            return Promise.reject(this.shouldReject);
        } else if (this.queue.length > 0) {
            const el = this.queue.shift()!!;
            return Promise.resolve(el);
        } else {
            return new Promise((resolve, reject) => {
                this.resolve_ = resolve; this.reject_ = reject;
            });
        }
    }
    return?(value?: any): Promise<IteratorResult<T, T>> {
        this.unsubscribeCallback();
        return this.next();
    }
    throw?(e?: any): Promise<IteratorResult<T, T>> {
        this.unsubscribeCallback();
        return this.next();
    }
    [Symbol.asyncIterator]() {
        return this;
    }
}

export abstract class RpcPeerConn<OutgoingCallContext, IncomingCallContext extends {remoteContext: any, src: string, signedWith: Uint8Array | null}> extends AbstractRpcTransmitter<OutgoingCallContext> {
    private messageId: number = 1;
    private questions: { [key: number]: [{resolve: Function, reject: Function}, string] } = {};
    private iters: { [key: number]: [AsyncIterator<any, any, undefined>, string] } = {};

    private _peerCapabilities: string[] | null = null;
    public get peerCapabilities(): string[] | null { return this._peerCapabilities; }

    readonly errorOccured = signal<(errMes: string) => void>();
    readonly connected = signal<(helloParams: any, context: IncomingCallContext) => void>();

    constructor(private broker: AbstractRpcTransmitter<IncomingCallContext>, protected crypto: ICrypto, public peerName: string) {
        super();
        if (typeof crypto !== 'object') throw 'RpcPeerConn: invalid crypto helper supplied';
        this.errorOccured.connect(this.onErrorOccured.bind(this));
    }

    get outstanding() {
        return Object.keys(this.questions).length;
    }

    _handleIterResponse(iter, promise, requestId, name) {
        promise.then((response) => {
            if (response.done)
                this.__sendDataInternal([RpcOp.ANSWER, requestId, response.value]);
            else {
                this.__sendDataInternal([RpcOp.ANSWERITER, requestId, response.value]);
                this._handleIterResponse(iter, iter.next(), requestId, name);
            }
        }, (error) => {
            console.warn(`RPC Call "${name}" Failed (async):`,error);
            this.__sendDataInternal(translateError(requestId, error));
        })
    }
    abstract buildIncomingCallContext(remoteContext: any, src: string, signedWith: Uint8Array | null): IncomingCallContext
    async handleMessage(json: any[]) {
        if (json[0] == RpcOp.SIGNED) {
            const { key, data } = await this.crypto.verifyUnwrap('signed', json[1]);
            if (rpcTrace) rpcTrace(this.peerName, "<", key, data);
            /*if (this.peerKey && !isEqualBytesConstTime(this.peerKey, key)) {
                console.warn("received data signed with unexpected key, ignoring");
            } else {*/
                this.handleUnwrappedMessage(data, key);
            //}
        } else {
            if (rpcTrace) rpcTrace(this.peerName, "<", null, json);
            this.handleUnwrappedMessage(json, null);
        }
    }
    handleUnwrappedMessage(json: any[], signedWith: Uint8Array | null) {
        if (json[0] === RpcOp.ANSWER) {
            const [_, requestId, result] = json;
            if (!(requestId in this.questions)) throw `invalid request id ${requestId} (timeout?)`;
            var [{resolve, reject}, name] = this.questions[requestId];
            delete this.questions[requestId];
            resolve(result);
        } else if (json[0] === RpcOp.ANSWERITER) {
            const [_, requestId, result] = json;
            if (!(requestId in this.questions)) throw `invalid request id ${requestId} (timeout?)`;
            var [{resolve, reject}, name] = this.questions[requestId];
            resolve(result, false);
        } else if (json[0] === RpcOp.ANSWERERROR) {
            const [_, requestId, errNo, error] = json;
            console.warn("RPC Response Error: ",requestId,errNo,error);
            if (!(requestId in this.questions)) throw `invalid request id ${requestId} (timeout?)`;
            var [{resolve, reject}, name] = this.questions[requestId];
            delete this.questions[requestId];
            reject(new RpcRemoteError(errNo, error, name));
        } else if (json[0] === RpcOp.METHODCALL || json[0] === RpcOp.INTROSPECT || json[0] === RpcOp.EVENT || json[0] === RpcOp.GETPROP || json[0] === RpcOp.SETPROP || json[0] === RpcOp.SUBSCRIBE) {
            const [opCode, requestId, nodeId, objectId, interfaceId, method, args, remoteContext] = json;
            try {
                const context = this.buildIncomingCallContext(remoteContext, this.peerName + '/' + (remoteContext?.p || ''), signedWith);
                if (!requestId) {
                    this.broker.sendRpcCallNoResponse(opCode, nodeId, objectId, interfaceId, method, args, context);
                } else {
                    let result = this.broker.sendRpcCall(opCode, nodeId, objectId, interfaceId, method, args, context);
                    if ('then' in result) {
                        result.then((response) => {
                            this.__sendDataInternal([RpcOp.ANSWER, requestId, response]);
                        }, (error) => {
                            console.warn(`RPC Call "${objectId}.${interfaceId}.${method}" Failed (async):`,error);
                            this.__sendDataInternal(translateError(requestId, error));
                        });
                    } else if ('next' in result) {
                        this.iters[requestId] = [result, `${nodeId}.${objectId}.${interfaceId}.${method}`];
                        this._handleIterResponse(result, result.next(), requestId, `${nodeId}.${objectId}.${interfaceId}.${method}`);
                    } else {
                        throw 'invalid result from _rpcCall';
                    }
                }
            } catch(error) {
                console.warn(`RPC Call "${objectId}.${interfaceId}.${method}" Failed (sync):`,error);
                if (requestId) this.__sendDataInternal(translateError(requestId, error));
            }
        } else if (json[0] === RpcOp.UNSUBSCRIBE) {
            const [_, requestId, ...__] = json;
            const [iter, name] = this.iters[requestId];
            if (iter.return)
                this._handleIterResponse(iter, iter.return(), requestId, name);
            else
                this.__sendDataInternal([RpcOp.ANSWER, requestId, undefined]);
            delete this.iters[requestId];
        } else if (json[0] === RpcOp.FAIL) {
            const [_, errNo, error] = json;
            console.log("Server FAIL:",json)
            this.errorOccured.emit(error?.server_error || error);
        } else if (json[0] === RpcOp.PING) {
            const [_, ts] = json;
            this.__sendDataInternal([RpcOp.PONG, ts, +new Date()]);
        } else if (json[0] === RpcOp.HELLO) {
            const context = this.buildIncomingCallContext(null, this.peerName, signedWith);
            const [_, errNo, data] = json;
            this._peerCapabilities = data.supports ?? [];
            this._handlePeerHello(data, context);
        }
    }

    sendRpcCall(opCode: RpcOp, nodeId: string, objectId: string, interfaceId: string, method: string, args: any[], context: OutgoingCallContext, timeout: number = 0): Promise<any> | AsyncIterator<any> {
        if (opCode == RpcOp.METHODCALL || opCode == RpcOp.SETPROP || opCode == RpcOp.GETPROP || opCode == RpcOp.INTROSPECT) {
            return new Promise((resolve, reject) => {
                const id = this.messageId++;
                const name = `${nodeId}.${objectId}.${interfaceId}.${method}`;
                this.questions[id] = [{resolve, reject}, name];
                if (!this.__sendDataInternal([opCode, id, nodeId, objectId, interfaceId, method, args, this._translateOutgoingCallContext(context)])) {
                    try {
                        if (!timeout) throw new RpcConnFailed(9998, 'websocket failure', name);
                        Promise.race([
                            rejectAfterTimeout(timeout, new RpcConnFailed(9997, 'websocket failure', name)),
                            this._getReconnectPromise(),
                        ]).catch(reject).then(() => {
                            if (!this.__sendDataInternal([opCode, id, nodeId, objectId, interfaceId, method, args, this._translateOutgoingCallContext(context)]))
                                reject('websocket failure');
                        })
                    } catch(e) {
                        delete this.questions[id]; throw e;
                    }
                }
                if (timeout) setTimeout(() => { delete this.questions[id]; reject(new RpcTimeout(10000, 'timeout', name)); }, timeout);
            });
        } else if (opCode == RpcOp.SUBSCRIBE) {
            const proxy = new AsyncIteratorProxy(() => {
                this.__sendDataInternal([RpcOp.UNSUBSCRIBE, id, nodeId, objectId, interfaceId, method, args, this._translateOutgoingCallContext(context)]);
                delete this.questions[id];
            });
            const id = this.messageId++;
            this.questions[id] = [{
                resolve: (value, done = true) => {
                    proxy.push(value, done);
                    if (done) delete this.questions[id];
                },
                reject: (err) => {
                    proxy.reject(err);
                    delete this.questions[id];
                }
            }, `${nodeId}.${objectId}.${interfaceId}.${method}`];
            if (!this.__sendDataInternal([opCode, id, nodeId, objectId, interfaceId, method, args, this._translateOutgoingCallContext(context)])) {
                delete this.questions[id]; proxy.reject('websocket failure');
            }
            return proxy;
        } else {
            throw "invalid rpc call opcode";
        }
    }
    sendRpcCallNoResponse(opCode: RpcOp, nodeId: string, objectId: string, interfaceId: string, method: string, args: any[], context: OutgoingCallContext) {
        if (!(opCode == RpcOp.EVENT || opCode == RpcOp.METHODCALL || opCode == RpcOp.SETPROP || opCode == RpcOp.GETPROP || opCode == RpcOp.INTROSPECT)) throw "invalid rpc call opcode";
        this.__sendDataInternal([opCode, 0, nodeId, objectId, interfaceId, method, args, this._translateOutgoingCallContext(context)]);
    }
    __sendDataInternal(data: any[]): boolean {
        if (rpcTrace) rpcTrace(this.peerName, ">", null, data);
        return this._sendDataInternal(data);
    }
    abstract _sendDataInternal(data: any[]): boolean
    abstract _translateOutgoingCallContext(ctx: OutgoingCallContext): any
    abstract _handlePeerHello(helloParams: any, context: IncomingCallContext)

    _reconnectPromise?: Promise<void>;
    _reconnectResolve: Function;
    _getReconnectPromise(): Promise<void> {
        if (!this._reconnectPromise) {
            this._reconnectPromise = new Promise((res, rej) => {
                const dis = this.connected.on((_, __) => {
                    dis[Symbol.dispose]();
                    this._reconnectPromise = undefined;
                    res();
                });
            });
        }
        return this._reconnectPromise;
    }

    onErrorOccured(errMes: string) {
        for(const k in this.questions) {
            const [{reject}, name] = this.questions[k];
            reject(new RpcConnFailed(9999, errMes, name));
        }
        this.questions = {};
    }
}
function rejectAfterTimeout(timeout, reason): Promise<void> {
    return new Promise((res, rej) => setTimeout(() => rej(reason), timeout));
}
type IntrospectionMethodInfo = {
    methodName: string
};
type IntrospectionPropertyInfo = {
    name: string,
    get: boolean,
    set: boolean,
};
type KvIntrospectionInterfaceInfo = {
    interfaceId: string,
    methods: { [methodName: string]: IntrospectionMethodInfo },
    properties: { [propertyName: string]: IntrospectionPropertyInfo },
}
export class LocalCallable<Context> implements RpcCallableNode<Context> {
    readonly rpcObjects : {[key:string]: {ref: any, interfaces: { [key:string]: KvIntrospectionInterfaceInfo } } } = {};

    constructor(registerObjects : {[key:string]: {ref: any, interfaces: string[] } } = {}) {
        for(const k in registerObjects) {
            this.registerObject(k, registerObjects[k].interfaces, registerObjects[k].ref);
        }
    }
    
    _eventSignals : {[key: string]: SignalFunc<any[], void> } = {};
    event<T extends any[]>(objectId: string, interfaceId: string, eventName: string): SignalFunc<[Context, ...T], void> {
        let name = `${objectId}:${interfaceId}:${eventName}`;
        if (!(name in this._eventSignals)) this._eventSignals[name] = signal();
        return this._eventSignals[name] as SignalFunc<[Context, ...T], void>;
    }

    registerObject(objectId: string, interfaceIds: string[], handlerObject: any): Disposable {
        const {methods, properties} = getAllMethods(handlerObject);
        this.rpcObjects[objectId] = { ref: handlerObject, interfaces: Object.fromEntries(interfaceIds.map(interfaceId => [interfaceId, { interfaceId, methods, properties }])) };
        return {
            [Symbol.dispose]: () => {
                if (this.rpcObjects[objectId]?.ref == handlerObject)
                    delete this.rpcObjects[objectId];
            }
        };
    }

    getObject(objectId: string, interfaceId: string, method: string, mtype: 'methods' | 'properties', atype: 'methodName' | 'get' | 'set', context: Context): any {
        const obj = this.rpcObjects[objectId];
        if (!obj) throw `object id "${objectId}" not found`;
        if (!obj.interfaces[interfaceId]) throw `interface "${objectId}.${interfaceId}" not found`;
        if (obj.interfaces[interfaceId][mtype] && !obj.interfaces[interfaceId][mtype][method]?.[atype]) throw `no ${mtype} ${atype} = "${method}" in interface "${objectId}.${interfaceId}"`;
        return obj.ref;
    }

    _getIntrospectionInfo() {
        return Object.entries(this.rpcObjects).map(([x, {ref, interfaces}]) => ({
            objectId: x, interfaces: Object.values(interfaces).map(({interfaceId, methods, properties}) => ({
                interfaceId, methods: Object.values(methods), properties: Object.values(properties)
            })),
        }));
    }

    executeRpcCall(opCode: RpcOp, objectId: string, interfaceId: string, method: string, args: any[], context: Context, timeout?: number): Promise<any> | AsyncIterator<any> {
        if (opCode == RpcOp.METHODCALL) {
            const obj = this.getObject(objectId, interfaceId, method, 'methods', 'methodName', context);
            if (!(method in obj)) throw `method "${objectId}.${interfaceId}.${method}" not found`;
            return Promise.resolve(obj[method].call(obj, context, ...args));
        } else if (opCode == RpcOp.SUBSCRIBE) {
            const obj = this.getObject(objectId, interfaceId, method, 'methods', 'methodName', context);
            if (!(method in obj)) throw `method "${objectId}.${interfaceId}.${method}" not found`;
            return obj[method].call(obj, context, ...args) as AsyncIterator<any>;
        } else if (opCode == RpcOp.GETPROP) {
            const obj = this.getObject(objectId, interfaceId, method, 'properties', 'get', context);
            return Promise.resolve(obj[method]);
        } else if (opCode == RpcOp.SETPROP) {
            const obj = this.getObject(objectId, interfaceId, method, 'properties', 'set', context);
            obj[method] = args[0];
            return Promise.resolve(undefined);
        } else if (opCode == RpcOp.INTROSPECT) {
            return Promise.resolve(this._getIntrospectionInfo());
        } else {
            throw "opcode not supported";
        }
    }
    executeRpcCallNoResponse(opCode: RpcOp, objectId: string, interfaceId: string, method: string, args: any[], context: Context) {
        if (opCode == RpcOp.EVENT) {
            this.event(objectId, interfaceId, method).emit(context, ...args);
        } else if (opCode == RpcOp.METHODCALL) {
            const obj = this.getObject(objectId, interfaceId, method, 'methods', 'methodName', context);
            if (!(method in obj)) throw `method "${objectId}.${interfaceId}.${method}" not found`;
            obj[method].call(obj, context, ...args);
        } else if (opCode == RpcOp.SETPROP) {
            const obj = this.getObject(objectId, interfaceId, method, 'properties', 'set', context);
            obj[method] = args[0];
            return undefined;
        } else {
            throw "opcode not supported";
        }
    }
}
function getAllMethods(obj: any): { methods: {[n: string]: IntrospectionMethodInfo}, properties: {[n: string]: IntrospectionPropertyInfo} } {
    let curr = obj, methods = {}, properties = {};
    do {
        var props = Object.getOwnPropertyDescriptors(curr);
        for (const prop in props) {
            if (typeof prop !== 'string') continue;
            if (typeof props[prop].value === 'function') {
                if (prop === 'constructor') continue;
                if (prop in methods) continue;
                methods[prop] = { methodName: prop };
            } else if (typeof props[prop].get === 'function' || typeof props[prop].set === 'function') {
                if (prop in properties) continue;
                properties[prop] = { name: prop, get: typeof props[prop].get === 'function', set: typeof props[prop].set === 'function' };
            }
        }
    } while((curr = Object.getPrototypeOf(curr)) && curr != Object.prototype);
    return { methods, properties };
}
if ('Buffer' in globalThis) {
    Buffer.prototype.toJSON = null;
}
export function tryStringify(json) {
    try {
        return JSON.stringify(json, (key, value) => {
            if (value instanceof Uint8Array || ('Buffer' in globalThis && value instanceof Buffer)) return "<<"+bufferToHex(value)+">>";
            else return value;
        });
    } catch(ex) {
        return "JSON.stringify failed ("+ex+")";
    }
}
export function dumpRpcMessage(json) {
    if (typeof json !== 'object' || !(json instanceof Array) || (json.length < 1)) {
        return "Invalid root object (type="+typeof json+", str="+json+"): "+JSON.stringify(json);
    }
    return (RpcOpNames[json[0]] || `UNK_${json[0]}}`) + tryStringify(json);
}

type TraceReceiverFn = (peer: string, direction: string, signedWith: Uint8Array | null, data: any[]) => void;
export function setRpcTraceReceiver(receiver: TraceReceiverFn | null) {
    rpcTrace = receiver;
}
let rpcTrace : TraceReceiverFn | null = null;
